import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { translating } from '../i18n/resolver.js';
import { useT } from '../i18n/useT.js';
import type { MessageKey } from '../i18n/da.js';
import { useAuth } from '../auth/useAuth.js';
import {
  useApproveInboundSender,
  useInboundAddress,
  useInboundMessages,
  useInboundSenders,
  useRevokeInboundSender,
  useSetInboundAddress,
} from '../api/hooks.js';
import type { InboundMessage, InboundOutcome } from '../api/hooks.js';

/**
 * Indgående post — documents arriving by email.
 *
 * A new page rather than a section of Indstillinger, following the rule that a
 * new feature gets a page and no existing page is restructured. It also is not
 * really settings: two thirds of it is a log of what happened.
 *
 * **The refused messages are the reason this screen exists.** A supplier sending
 * from an unapproved address would otherwise vanish, and the invoice would be
 * missing from the books with nothing to say it had been sent. So a refusal is
 * shown with its reason and a way to act on it — approve the sender, right
 * there.
 *
 * The page also says two things it would be easier to leave implied: that
 * nothing can reach the address until a receiving domain is configured, and
 * that an accepted document changes nothing until a person books it.
 */

const OUTCOME_LABELS: Record<InboundOutcome, MessageKey> = {
  accepted: 'inbound.outcome.accepted',
  sender_not_allowed: 'inbound.outcome.senderNotAllowed',
  duplicate: 'inbound.outcome.duplicate',
  unknown_address: 'inbound.outcome.unknownAddress',
  partially_accepted: 'inbound.outcome.partial',
  no_attachments: 'inbound.outcome.noAttachments',
};

/** Orange for the ones a person has to do something about. */
const OUTCOME_COLOURS: Record<InboundOutcome, string> = {
  accepted: 'green',
  sender_not_allowed: 'orange',
  duplicate: 'gray',
  unknown_address: 'orange',
  partially_accepted: 'orange',
  no_attachments: 'gray',
};

const senderSchema = z.object({
  email: z.email('inbound.emailInvalid'),
  note: z.string().trim().max(255),
});

type SenderValues = z.infer<typeof senderSchema>;

const addressSchema = z.object({
  localPart: z
    .string()
    .trim()
    .regex(
      /^[a-z0-9]([a-z0-9._-]{0,62}[a-z0-9])?$/u,
      'inbound.localPartInvalid',
    ),
});

type AddressValues = z.infer<typeof addressSchema>;


/** `2026-09-04T08:15:00Z` as `04.09.2026 08:15`, without constructing a Date. */
function danishTimestamp(iso: string): string {
  const [date, time] = iso.split('T');
  const [year, month, day] = (date ?? '').split('-');
  return `${day ?? ''}.${month ?? ''}.${year ?? ''} ${(time ?? '').slice(0, 5)}`;
}

export function InboundEmailPage(): ReactNode {
  const t = useT();
  const { role } = useAuth();
  const mayConfigure = role === 'owner' || role === 'system_admin';

  const address = useInboundAddress();
  const senders = useInboundSenders();
  const [outcome, setOutcome] = useState<'all' | InboundOutcome>('all');
  const messages = useInboundMessages(outcome === 'all' ? {} : { outcome });

  const setAddress = useSetInboundAddress();
  const approve = useApproveInboundSender();
  const revoke = useRevokeInboundSender();

  const addressForm = useForm<AddressValues>({
    mode: 'uncontrolled',
    initialValues: { localPart: '' },
    validate: translating(addressSchema, t, 'inbound'),
  });
  const senderForm = useForm<SenderValues>({
    mode: 'uncontrolled',
    initialValues: { email: '', note: '' },
    validate: translating(senderSchema, t, 'inbound'),
  });

  function handleAddress(values: AddressValues): void {
    setAddress.mutate(
      { localPart: values.localPart },
      {
        onSuccess: (saved) => {
          notifications.show({
            color: 'green',
            title: t('inbound.addressSet'),
            message: saved.address ?? saved.localPart,
          });
        },
      },
    );
  }

  function handleApprove(values: SenderValues): void {
    approve.mutate(
      {
        email: values.email.trim(),
        ...(values.note.trim() === '' ? {} : { note: values.note.trim() }),
      },
      {
        onSuccess: (saved) => {
          notifications.show({
            color: 'green',
            title: t('inbound.approved'),
            message: saved.email,
          });
          senderForm.reset();
        },
      },
    );
  }

  return (
    <Stack gap="lg">
      <Title order={2}>{t('inbound.title')}</Title>

      <Alert color="gray" variant="light" title={t('inbound.whatTitle')}>
        {t('inbound.what')}
      </Alert>

      {/* ---------------------------------------------------------------- */}
      {/* The address */}
      {/* ---------------------------------------------------------------- */}
      <Card withBorder padding="md">
        <Stack gap="md">
          <Text fw={500}>{t('inbound.address')}</Text>

          {address.isPending && (
            <Center p="md">
              <Loader size="sm" />
            </Center>
          )}

          {address.isError && (
            <ErrorMessage error={address.error} title={t('inbound.addressFailed')} />
          )}

          {address.data === null && !address.isPending && (
            <Text size="sm" c="dimmed">
              {t('inbound.noAddress')}
            </Text>
          )}

          {address.data != null && address.data.address !== null && (
            <Group gap="xs">
              <Code>{address.data.address}</Code>
              {!address.data.isActive && (
                <Badge color="gray" variant="light">
                  {t('inbound.inactive')}
                </Badge>
              )}
            </Group>
          )}

          {address.data != null && address.data.address === null && (
            /*
              The local part is set and no receiving domain is configured, so
              nothing can arrive. Showing the local part alone would look like a
              working address.
            */
            <Alert color="orange" variant="light" title={t('inbound.noDomainTitle')}>
              {t('inbound.noDomainA')}
              <Code>{address.data.localPart}</Code>
              {t('inbound.noDomainB')}
            </Alert>
          )}

          {mayConfigure ? (
            /*
              noValidate: the browser's own constraint validation blocks the
              submit event before Mantine sees it, so the Zod schema never runs.
            */
            <form noValidate onSubmit={addressForm.onSubmit(handleAddress)}>
              <Group align="flex-end" gap="sm" wrap="wrap">
                {/*
                  Empty, not prefilled with the current value.

                  Prefilling read as "this is the address" while behaving as
                  "type a new one", and it cost a real bug: `setValues` during
                  render bumps Mantine's field key, the input remounts, and a
                  typed edit is silently replaced by the old value — which then
                  gets submitted. The current address is displayed above this
                  field; the field is for the new one.
                */}
                <TextInput
                  label={t('inbound.newAddress')}
                  description={t('inbound.newAddressHelp')}
                  placeholder={address.data?.localPart ?? 'bilag'}
                  style={{ flex: 1, minWidth: 240 }}
                  key={addressForm.key('localPart')}
                  {...addressForm.getInputProps('localPart')}
                />
                <Button type="submit" loading={setAddress.isPending}>
                  {t('inbound.saveAddress')}
                </Button>
              </Group>
            </form>
          ) : (
            <Text size="sm" c="dimmed">
              {t('inbound.ownerOnly')}
            </Text>
          )}

          {setAddress.isError && (
            <ErrorMessage error={setAddress.error} title={t('inbound.addressNotSet')} />
          )}
        </Stack>
      </Card>

      {/* ---------------------------------------------------------------- */}
      {/* Approved senders */}
      {/* ---------------------------------------------------------------- */}
      <Card withBorder padding="md">
        <Stack gap="md">
          <Text fw={500}>{t('inbound.senders')}</Text>
          <Text size="sm" c="dimmed">
            {t('inbound.sendersHelp')}
          </Text>

          {senders.isError && (
            <ErrorMessage error={senders.error} title={t('inbound.sendersFailed')} />
          )}

          {senders.data !== undefined && senders.data.items.length === 0 && (
            <Alert color="orange" variant="light" title={t('inbound.noSendersTitle')}>
              {t('inbound.noSenders')}
            </Alert>
          )}

          {senders.data !== undefined && senders.data.items.length > 0 && (
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('inbound.col.email')}</Table.Th>
                  <Table.Th>{t('inbound.col.note')}</Table.Th>
                  {mayConfigure && <Table.Th aria-label={t('inbound.actions')} />}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {senders.data.items.map((sender) => (
                  <Table.Tr key={sender.id}>
                    <Table.Td>{sender.email}</Table.Td>
                    <Table.Td>{sender.note ?? ''}</Table.Td>
                    {mayConfigure && (
                      <Table.Td>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={t('inbound.removeSender', { email: sender.email })}
                          loading={revoke.isPending}
                          onClick={() => {
                            revoke.mutate(sender.id, {
                              onSuccess: () => {
                                notifications.show({
                                  color: 'gray',
                                  title: t('inbound.revoked'),
                                  message: sender.email,
                                });
                              },
                            });
                          }}
                        >
                          ×
                        </ActionIcon>
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}

          {mayConfigure && (
            <form noValidate onSubmit={senderForm.onSubmit(handleApprove)}>
              <Group align="flex-end" gap="sm" wrap="wrap">
                <TextInput
                  label={t('inbound.approveSender')}
                  placeholder={t('inbound.senderPlaceholder')}
                  style={{ flex: 1, minWidth: 240 }}
                  key={senderForm.key('email')}
                  {...senderForm.getInputProps('email')}
                />
                <TextInput
                  label={t('inbound.col.note')}
                  placeholder={t('inbound.notePlaceholder')}
                  style={{ flex: 1, minWidth: 180 }}
                  key={senderForm.key('note')}
                  {...senderForm.getInputProps('note')}
                />
                <Button type="submit" loading={approve.isPending}>
                  {t('inbound.approve')}
                </Button>
              </Group>
            </form>
          )}

          {approve.isError && (
            <ErrorMessage error={approve.error} title={t('inbound.approveFailed')} />
          )}
          {revoke.isError && (
            <ErrorMessage error={revoke.error} title={t('inbound.revokeFailed')} />
          )}
        </Stack>
      </Card>

      {/* ---------------------------------------------------------------- */}
      {/* The log */}
      {/* ---------------------------------------------------------------- */}
      <Card withBorder padding="md">
        <Stack gap="md">
          <Group justify="space-between" align="center" wrap="wrap">
            <Text fw={500}>{t('inbound.log')}</Text>
            <SegmentedControl
              value={outcome}
              onChange={(value) => {
                setOutcome(value as 'all' | InboundOutcome);
              }}
              data={[
                { label: t('inbound.filter.all'), value: 'all' },
                { label: t('inbound.filter.accepted'), value: 'accepted' },
                { label: t('inbound.filter.rejected'), value: 'sender_not_allowed' },
              ]}
            />
          </Group>

          {messages.isPending && (
            <Center p="md">
              <Loader size="sm" />
            </Center>
          )}

          {messages.isError && (
            <ErrorMessage error={messages.error} title={t('inbound.logFailed')} />
          )}

          {messages.data !== undefined && messages.data.items.length === 0 && (
            <Text size="sm" c="dimmed">
              {t('inbound.logEmpty')}
            </Text>
          )}

          {messages.data !== undefined && messages.data.items.length > 0 && (
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('inbound.col.received')}</Table.Th>
                  <Table.Th>{t('inbound.col.from')}</Table.Th>
                  <Table.Th>{t('inbound.col.subject')}</Table.Th>
                  <Table.Th>{t('inbound.col.files')}</Table.Th>
                  <Table.Th>{t('inbound.col.outcome')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {messages.data.items.map((item) => (
                  <MessageRow
                    key={item.id}
                    message={item}
                    mayApprove={mayConfigure}
                    onApprove={(email) => {
                      approve.mutate(
                        { email },
                        {
                          onSuccess: () => {
                            notifications.show({
                              color: 'green',
                              title: t('inbound.approved'),
                              message: t('inbound.approvedDetail', { email }),
                            });
                          },
                        },
                      );
                    }}
                  />
                ))}
              </Table.Tbody>
            </Table>
          )}

          <Text size="sm" c="dimmed">
            {t('inbound.logNote')}
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}

function MessageRow({
  message,
  mayApprove,
  onApprove,
}: {
  readonly message: InboundMessage;
  readonly mayApprove: boolean;
  readonly onApprove: (email: string) => void;
}): ReactNode {
  const t = useT();
  return (
    <Table.Tr>
      <Table.Td>{danishTimestamp(message.receivedAt)}</Table.Td>
      <Table.Td>{message.fromEmail}</Table.Td>
      <Table.Td>{message.subject ?? ''}</Table.Td>
      <Table.Td>
        {message.attachmentCount === 0
          ? '—'
          : `${String(message.storedCount)}/${String(message.attachmentCount)}`}
      </Table.Td>
      <Table.Td>
        <Stack gap={4}>
          <Badge color={OUTCOME_COLOURS[message.outcome]} variant="light">
            {t(OUTCOME_LABELS[message.outcome])}
          </Badge>
          {/* The reason, never truncated: it is the actionable part. */}
          {message.detail !== null && (
            <Text size="xs" c="dimmed">
              {message.detail}
            </Text>
          )}
          {/*
            The refusal and its remedy in one place. Approving here does not
            re-deliver anything -- the message is gone -- so the toast says to
            ask the sender again rather than implying it will reappear.
          */}
          {message.outcome === 'sender_not_allowed' && mayApprove && (
            <Button
              size="compact-xs"
              variant="light"
              onClick={() => {
                onApprove(message.fromEmail);
              }}
            >
              {t('inbound.approveThis', { email: message.fromEmail })}
            </Button>
          )}
        </Stack>
      </Table.Td>
    </Table.Tr>
  );
}
